import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { compact, date, dateTime, money, money0, qty, titleCase, unitLabel } from '../lib/format';
import { PageHeader, Tabs } from '../components/Layout';
import {
  Drawer,
  Empty,
  ErrorState,
  Kpi,
  Loading,
  Modal,
  Select,
  StatusChip,
  TextInput,
  useToast,
} from '../components/ui';

interface PoLine {
  id: string;
  ingredientId: string;
  code: string;
  name: string;
  unit: string;
  /** Days this item keeps; 0 for something that does not go off. */
  shelfLifeDays: number;
  quantity: number;
  receivedQty: number;
  pendingQty: number;
  rate: number;
  gstRate: number;
  amount: number;
}

interface PurchaseOrder {
  id: string;
  number: string;
  status: string;
  supplier: string;
  supplierId: string;
  supplierGstin: string | null;
  paymentTerms: string;
  orderDate: string;
  expectedDate: string;
  notes: string | null;
  subtotal: number;
  tax: number;
  total: number;
  lines: PoLine[];
}

interface Supplier {
  id: string;
  name: string;
  gstin: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
  paymentTerms: string;
  rating: number;
  isActive: boolean;
  orderCount: number;
  spend: number;
}

/** A stock item as the store screen lists it, for picking order lines. */
interface Ingredient {
  id: string;
  name: string;
  unit: string;
  stock: number;
  parLevel: number;
  avgCost: number;
}

interface Suggestion {
  id: string;
  code: string;
  name: string;
  unit: string;
  stock: number;
  reorderLevel: number;
  parLevel: number;
  avgCost: number;
  gstRate: number;
  suggestedQty: number;
}

interface Grn {
  id: string;
  number: string;
  supplier: string;
  purchaseOrderNo: string | null;
  supplierInvoiceNo: string | null;
  total: number;
  lineCount: number;
  receivedAt: string;
}

export function ProcurementPage() {
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const [tab, setTab] = useState<'orders' | 'suppliers' | 'receipts'>('orders');
  /*
   * A notification can name the thing it is about, so clicking one opens it
   * rather than dropping somebody on a board to find it again themselves.
   */
  const [params, setParams] = useSearchParams();
  const [openId, setOpenId] = useState<string | null>(params.get('open'));

  useEffect(() => {
    const wanted = params.get('open');
    if (wanted) setOpenId(wanted);
  }, [params]);

  const closeDrawer = () => {
    setOpenId(null);
    if (params.get('open')) {
      const next = new URLSearchParams(params);
      next.delete('open');
      setParams(next, { replace: true });
    }
  };
  const [drafting, setDrafting] = useState(false);
  const [addingSupplier, setAddingSupplier] = useState(false);

  const orders = useQuery({
    queryKey: ['purchase-orders'],
    queryFn: () => api.get<PurchaseOrder[]>('/api/purchase-orders'),
  });
  const suppliers = useQuery({
    queryKey: ['suppliers'],
    queryFn: () => api.get<Supplier[]>('/api/suppliers'),
  });
  const suggestions = useQuery({
    queryKey: ['reorder'],
    queryFn: () => api.get<Suggestion[]>('/api/reorder-suggestions'),
  });
  const receipts = useQuery({
    queryKey: ['goods-receipts'],
    queryFn: () => api.get<Grn[]>('/api/goods-receipts'),
    enabled: tab === 'receipts',
  });

  if (orders.isLoading) return <Loading label="Loading procurement…" />;
  if (orders.error)
    return <ErrorState error={orders.error} onRetry={() => void orders.refetch()} />;

  const rows = orders.data ?? [];
  const open = rows.filter((row) =>
    ['PENDING_APPROVAL', 'APPROVED', 'PARTIALLY_RECEIVED'].includes(row.status),
  );
  const received = rows.filter((row) => row.status === 'RECEIVED');
  const suggestedValue = (suggestions.data ?? []).reduce(
    (sum, item) => sum + item.suggestedQty * item.avgCost,
    0,
  );

  return (
    <div className="stack">
      <PageHeader
        title="Procurement"
        subtitle="Reorder suggestions → approval → purchase order → goods receipt"
        actions={
          <>
            {can('procurement:create') ? (
              <>
                <button className="btn" onClick={() => setAddingSupplier(true)}>
                  + Supplier
                </button>
                <button className="btn primary" onClick={() => setDrafting(true)}>
                  Raise purchase order
                </button>
              </>
            ) : null}
          </>
        }
      />

      <div className="kpis">
        <Kpi
          label="To reorder"
          value={suggestions.data?.length ?? 0}
          foot={`${compact(suggestedValue)} to reach par`}
          tone={(suggestions.data?.length ?? 0) > 0 ? 'amber' : ''}
        />
        <Kpi
          label="Open orders"
          value={open.length}
          foot={`${compact(open.reduce((s, o) => s + o.total, 0))} committed`}
          tone="blue"
        />
        <Kpi
          label="Received value"
          value={compact(received.reduce((s, o) => s + o.total, 0))}
          foot={`${received.length} closed orders`}
        />
        <Kpi
          label="Suppliers"
          value={suppliers.data?.length ?? 0}
          foot={`Average rating ${((suppliers.data ?? []).reduce((s, x) => s + x.rating, 0) / Math.max(suppliers.data?.length ?? 1, 1)).toFixed(1)}`}
        />
      </div>

      <Tabs
        tabs={[
          { id: 'orders', label: 'Purchase orders' },
          { id: 'suppliers', label: 'Suppliers' },
          { id: 'receipts', label: 'Goods receipts' },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === 'orders' ? (
        <div className="card">
          {(suggestions.data?.length ?? 0) > 0 && can('procurement:create') ? (
            <div
              className="list-row"
              style={{ background: 'var(--warn-soft)', borderBottom: '1px solid var(--line)' }}
            >
              <span className="chip warn">{suggestions.data!.length} items below reorder</span>
              <span className="small dim grow">
                Ordering {compact(suggestedValue)} would bring every one of them back to par level.
              </span>
              <button className="btn sm primary" onClick={() => setDrafting(true)}>
                Draft purchase order
              </button>
            </div>
          ) : null}

          {rows.length === 0 ? (
            <Empty icon="⇄" message="No purchase orders yet" />
          ) : (
            <div className="table-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>Supplier</th>
                    <th>Ordered</th>
                    <th>Expected</th>
                    <th className="num">Lines</th>
                    <th className="num">Value</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id} className="clickable" onClick={() => setOpenId(row.id)}>
                      <td className="b mono">{row.number}</td>
                      <td>{row.supplier}</td>
                      <td>{date(row.orderDate)}</td>
                      <td>{date(row.expectedDate)}</td>
                      <td className="num">{row.lines.length}</td>
                      <td className="num b">{money(row.total)}</td>
                      <td>
                        <StatusChip status={row.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}

      {tab === 'suppliers' ? (
        <div className="card">
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Supplier</th>
                  <th>GSTIN</th>
                  <th>Contact</th>
                  <th>Terms</th>
                  <th className="num">Orders</th>
                  <th className="num">Spend</th>
                  <th className="num">Rating</th>
                </tr>
              </thead>
              <tbody>
                {(suppliers.data ?? []).map((supplier) => (
                  <tr key={supplier.id}>
                    <td>
                      <div className="b">{supplier.name}</div>
                      <div className="xs muted">{supplier.address}</div>
                    </td>
                    <td className="mono xs">{supplier.gstin ?? '—'}</td>
                    <td className="small">
                      {supplier.phone}
                      <div className="xs muted">{supplier.email}</div>
                    </td>
                    <td>{titleCase(supplier.paymentTerms)}</td>
                    <td className="num">{supplier.orderCount}</td>
                    <td className="num">{money0(supplier.spend)}</td>
                    <td className="num">{'★'.repeat(Math.round(supplier.rating))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {tab === 'receipts' ? (
        <div className="card">
          {receipts.isLoading ? (
            <Loading />
          ) : (receipts.data?.length ?? 0) === 0 ? (
            <Empty message="Nothing received yet" />
          ) : (
            <div className="table-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Receipt</th>
                    <th>Received</th>
                    <th>Supplier</th>
                    <th>Against order</th>
                    <th>Supplier invoice</th>
                    <th className="num">Lines</th>
                    <th className="num">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {receipts.data!.map((grn) => (
                    <tr key={grn.id}>
                      <td className="b mono">{grn.number}</td>
                      <td>{dateTime(grn.receivedAt)}</td>
                      <td>{grn.supplier}</td>
                      <td className="mono xs">{grn.purchaseOrderNo ?? '—'}</td>
                      <td className="small">{grn.supplierInvoiceNo ?? '—'}</td>
                      <td className="num">{grn.lineCount}</td>
                      <td className="num b">{money(grn.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}

      {openId ? <OrderDrawer id={openId} onClose={closeDrawer} /> : null}

      {drafting ? (
        <DraftOrderModal
          suppliers={(suppliers.data ?? []).filter((s) => s.isActive)}
          suggestions={suggestions.data ?? []}
          onClose={() => setDrafting(false)}
          onDone={() => {
            setDrafting(false);
            void queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
          }}
        />
      ) : null}

      {addingSupplier ? (
        <SupplierModal
          onClose={() => setAddingSupplier(false)}
          onDone={() => {
            setAddingSupplier(false);
            void queryClient.invalidateQueries({ queryKey: ['suppliers'] });
          }}
        />
      ) : null}
    </div>
  );
}

function OrderDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { fail, push } = useToast();
  const { can } = useAuth();
  const [receiving, setReceiving] = useState(false);

  const order = useQuery({
    queryKey: ['purchase-order', id],
    queryFn: () => api.get<PurchaseOrder>(`/api/purchase-orders/${id}`),
  });

  const transition = useMutation({
    mutationFn: (action: string) => api.post(`/api/purchase-orders/${id}/transition`, { action }),
    onSuccess: () => {
      push('Order updated', 'ok');
      void queryClient.invalidateQueries({ queryKey: ['purchase-order', id] });
      void queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
    },
    onError: fail,
  });

  const data = order.data;

  return (
    <>
      <Drawer
        title={data ? `${data.number} · ${data.supplier}` : 'Purchase order'}
        size="wide"
        onClose={onClose}
        footer={
          data ? (
            <>
              <button className="btn" onClick={onClose}>
                Close
              </button>
              {data.status === 'DRAFT' && can('procurement:create') ? (
                <button className="btn primary" onClick={() => transition.mutate('SUBMIT')}>
                  Submit for approval
                </button>
              ) : null}
              {data.status === 'PENDING_APPROVAL' && can('procurement:approve') ? (
                <>
                  <button className="btn danger" onClick={() => transition.mutate('REJECT')}>
                    Reject
                  </button>
                  <button className="btn primary" onClick={() => transition.mutate('APPROVE')}>
                    Approve
                  </button>
                </>
              ) : null}
              {['APPROVED', 'PARTIALLY_RECEIVED'].includes(data.status) &&
              can('procurement:receive') ? (
                <button className="btn primary" onClick={() => setReceiving(true)}>
                  Receive goods
                </button>
              ) : null}
            </>
          ) : null
        }
      >
        {order.isLoading || !data ? (
          <Loading />
        ) : (
          <div className="stack">
            <div className="card">
              <div className="card-body row between wrap gap-16">
                <div>
                  <div className="row gap-6">
                    <StatusChip status={data.status} />
                    <span className="chip">{titleCase(data.paymentTerms)}</span>
                  </div>
                  <div className="small dim" style={{ marginTop: 6 }}>
                    Ordered {date(data.orderDate)} · expected {date(data.expectedDate)}
                  </div>
                  <div className="small muted">GSTIN {data.supplierGstin ?? '—'}</div>
                  {data.notes ? (
                    <div className="small" style={{ marginTop: 6 }}>
                      {data.notes}
                    </div>
                  ) : null}
                </div>
                <div className="right">
                  <div className="muted xs">Order value</div>
                  <div style={{ fontSize: 23, fontWeight: 750 }}>{money(data.total)}</div>
                  <div className="xs dim">including GST {money(data.tax)}</div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-head">
                <h2>Order lines</h2>
              </div>
              <div className="table-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th className="num">Ordered</th>
                      <th className="num">Received</th>
                      <th className="num">Rate</th>
                      <th className="num">GST</th>
                      <th className="num">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.lines.map((line) => (
                      <tr key={line.id}>
                        <td>
                          <div className="b">{line.name}</div>
                          <div className="xs muted mono">{line.code}</div>
                        </td>
                        <td className="num">{qty(line.quantity, line.unit)}</td>
                        <td className={`num ${line.receivedQty >= line.quantity ? 'b' : 'dim'}`}>
                          {qty(line.receivedQty, line.unit)}
                        </td>
                        <td className="num">{money(line.rate)}</td>
                        <td className="num dim">{line.gstRate}%</td>
                        <td className="num b">{money(line.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={5}>Taxable</td>
                      <td className="num">{money(data.subtotal)}</td>
                    </tr>
                    <tr>
                      <td colSpan={5}>GST</td>
                      <td className="num">{money(data.tax)}</td>
                    </tr>
                    <tr>
                      <td colSpan={5}>Total</td>
                      <td className="num">{money(data.total)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        )}
      </Drawer>

      {receiving && data ? (
        <ReceiveModal
          order={data}
          onClose={() => setReceiving(false)}
          onDone={() => {
            setReceiving(false);
            void queryClient.invalidateQueries({ queryKey: ['purchase-order', id] });
            void queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
            void queryClient.invalidateQueries({ queryKey: ['ingredients'] });
            void queryClient.invalidateQueries({ queryKey: ['menu'] });
          }}
        />
      ) : null}
    </>
  );
}

function ReceiveModal({
  order,
  onClose,
  onDone,
}: {
  order: PurchaseOrder;
  onClose: () => void;
  onDone: () => void;
}) {
  const { fail, push } = useToast();
  const pending = order.lines.filter((line) => line.pendingQty > 0.001);
  const [invoiceNo, setInvoiceNo] = useState('');
  /**
   * The expiry each delivery is being booked in with.
   *
   * Pre-filled from the item's shelf life so it is a date somebody confirms
   * rather than a field they have to work out; whoever is unpacking the crate
   * can read the printed date and correct it. Anything with no shelf life -
   * salt, foil, cleaning supplies - is left blank.
   */
  const defaultExpiry = (shelfLifeDays: number) =>
    shelfLifeDays > 0
      ? new Date(Date.now() + shelfLifeDays * 86_400_000).toISOString().slice(0, 10)
      : '';

  const [lines, setLines] = useState(
    pending.map((line) => ({
      purchaseOrderLineId: line.id,
      quantity: line.pendingQty,
      rate: line.rate,
      expiryDate: defaultExpiry(line.shelfLifeDays ?? 0),
    })),
  );

  const receive = useMutation({
    mutationFn: () =>
      api.post<{ number: string; total: number }>(`/api/purchase-orders/${order.id}/receive`, {
        supplierInvoiceNo: invoiceNo,
        lines,
      }),
    onSuccess: (result) => {
      push(`${result.number} posted · ${money0(result.total)} received`, 'ok');
      onDone();
    },
    onError: fail,
  });

  return (
    <Modal
      title={`Receive against ${order.number}`}
      size="wide"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn primary"
            disabled={receive.isPending}
            onClick={() => receive.mutate()}
          >
            Post goods receipt
          </button>
        </>
      }
    >
      <div className="col gap-16">
        <div className="row gap-6 wrap">
          <span className="chip brand">{order.supplier}</span>
          <span className="chip">{titleCase(order.paymentTerms)}</span>
          <span className="chip">Expected {date(order.expectedDate)}</span>
        </div>

        <TextInput
          label="Supplier invoice number"
          value={invoiceNo}
          onChange={(e) => setInvoiceNo(e.target.value)}
          placeholder="e.g. SVT/26-27/1182"
          className=""
        />

        <div className="table-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Item</th>
                <th className="num">Ordered</th>
                <th className="num">Pending</th>
                <th>Receiving</th>
                <th>Rate (₹)</th>
                <th>Expires</th>
              </tr>
            </thead>
            <tbody>
              {pending.map((line, index) => (
                <tr key={line.id}>
                  <td>
                    <div className="b">{line.name}</div>
                    <div className="xs muted mono">
                      {line.code} · {unitLabel(line.unit)}
                    </div>
                  </td>
                  <td className="num dim">{qty(line.quantity)}</td>
                  <td className="num">{qty(line.pendingQty)}</td>
                  <td>
                    <input
                      className="input"
                      type="number"
                      step="0.001"
                      style={{ width: 110 }}
                      value={lines[index]?.quantity ?? 0}
                      onChange={(e) => {
                        const next = [...lines];
                        next[index] = { ...next[index]!, quantity: Number(e.target.value) || 0 };
                        setLines(next);
                      }}
                    />
                  </td>
                  <td>
                    <input
                      className="input"
                      type="number"
                      step="0.01"
                      style={{ width: 110 }}
                      value={lines[index]?.rate ?? 0}
                      onChange={(e) => {
                        const next = [...lines];
                        next[index] = { ...next[index]!, rate: Number(e.target.value) || 0 };
                        setLines(next);
                      }}
                    />
                  </td>
                  <td>
                    <input
                      className="input"
                      type="date"
                      style={{ width: 150 }}
                      title={
                        line.shelfLifeDays
                          ? `Keeps ${line.shelfLifeDays} days`
                          : 'This item does not go off'
                      }
                      value={lines[index]?.expiryDate ?? ''}
                      onChange={(e) => {
                        const next = [...lines];
                        next[index] = { ...next[index]!, expiryDate: e.target.value };
                        setLines(next);
                      }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="chip info">
          Receiving books stock in and re-weights each item&apos;s moving-average cost, which flows
          straight into plate costs.
        </div>
      </div>
    </Modal>
  );
}

/**
 * Raising a purchase order.
 *
 * Low-stock suggestions seed the lines because that is the common case, but a
 * kitchen orders plenty that is not below its reorder level yet - a party next
 * week, a supplier's minimum order, a new dish. So anything in the store can be
 * added, and an order can be raised when nothing is low at all.
 */
function DraftOrderModal({
  suppliers,
  suggestions,
  onClose,
  onDone,
}: {
  suppliers: Supplier[];
  suggestions: Suggestion[];
  onClose: () => void;
  onDone: () => void;
}) {
  const { fail, push } = useToast();
  const [picking, setPicking] = useState('');

  const stock = useQuery({
    queryKey: ['ingredients'],
    queryFn: () => api.get<Ingredient[]>('/api/ingredients'),
  });
  const [supplierId, setSupplierId] = useState(suppliers[0]?.id ?? '');
  const [expectedDate, setExpectedDate] = useState(
    new Date(Date.now() + 2 * 86_400_000).toISOString().slice(0, 10),
  );
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState(
    suggestions.map((item) => ({
      ingredientId: item.id,
      name: item.name,
      unit: item.unit,
      quantity: item.suggestedQty,
      rate: item.avgCost,
    })),
  );

  const create = useMutation({
    mutationFn: () =>
      api.post<PurchaseOrder>('/api/purchase-orders', {
        supplierId,
        expectedDate,
        notes,
        lines: lines
          .filter((line) => line.quantity > 0)
          .map((line) => ({
            ingredientId: line.ingredientId,
            quantity: line.quantity,
            rate: line.rate,
          })),
      }),
    onSuccess: (po) => {
      push(`${po.number} drafted · ${money0(po.total)}`, 'ok');
      onDone();
    },
    onError: fail,
  });

  const total = lines.reduce((sum, line) => sum + line.quantity * line.rate, 0);
  const ordered = lines.filter((line) => line.quantity > 0);

  const addLine = (ingredientId: string) => {
    if (!ingredientId) return;
    const item = (stock.data ?? []).find((i) => i.id === ingredientId);
    if (!item || lines.some((line) => line.ingredientId === ingredientId)) return;
    setLines((current) => [
      ...current,
      {
        ingredientId: item.id,
        name: item.name,
        unit: item.unit,
        // Enough to reach par if that is meaningful, otherwise one unit to edit.
        quantity: Math.max(1, Math.round((item.parLevel - item.stock) * 1000) / 1000),
        rate: item.avgCost,
      },
    ]);
    setPicking('');
  };

  return (
    <Modal
      title="Raise purchase order"
      size="wide"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn primary"
            disabled={!supplierId || ordered.length === 0 || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending
              ? 'Creating…'
              : `Create draft order · ${ordered.length} line${ordered.length === 1 ? '' : 's'}`}
          </button>
        </>
      }
    >
      <div className="col gap-16">
        <div className="form-grid three">
          <Select
            label="Supplier *"
            value={supplierId}
            onChange={(e) => setSupplierId(e.target.value)}
          >
            {suppliers.map((supplier) => (
              <option key={supplier.id} value={supplier.id}>
                {supplier.name} · {titleCase(supplier.paymentTerms)}
              </option>
            ))}
          </Select>
          <TextInput
            label="Expected delivery"
            type="date"
            value={expectedDate}
            onChange={(e) => setExpectedDate(e.target.value)}
          />
          <TextInput
            label="Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Indent reference"
          />
        </div>

        <div className="row gap-6 wrap" style={{ alignItems: 'flex-end' }}>
          <Select
            label="Add an item"
            hint="Anything in your store, whether or not it is low"
            value={picking}
            onChange={(e) => addLine(e.target.value)}
            style={{ minWidth: 260 }}
          >
            <option value="">Choose a stock item…</option>
            {(stock.data ?? [])
              .filter((item) => !lines.some((line) => line.ingredientId === item.id))
              .map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name} · {unitLabel(item.unit)} · {money(item.avgCost)}
                </option>
              ))}
          </Select>
        </div>

        {lines.length === 0 ? (
          <Empty message="No lines yet — nothing is below its reorder level, so add what you need above." />
        ) : (
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Quantity</th>
                  <th>Rate (₹)</th>
                  <th className="num">Amount</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {lines.map((line, index) => (
                  <tr key={line.ingredientId}>
                    <td>
                      <div className="b">{line.name}</div>
                      <div className="xs muted">{unitLabel(line.unit)}</div>
                    </td>
                    <td>
                      <input
                        className="input"
                        type="number"
                        step="0.001"
                        style={{ width: 110 }}
                        value={line.quantity}
                        onChange={(e) => {
                          const next = [...lines];
                          next[index] = { ...line, quantity: Number(e.target.value) || 0 };
                          setLines(next);
                        }}
                      />
                    </td>
                    <td>
                      <input
                        className="input"
                        type="number"
                        step="0.01"
                        style={{ width: 110 }}
                        value={line.rate}
                        onChange={(e) => {
                          const next = [...lines];
                          next[index] = { ...line, rate: Number(e.target.value) || 0 };
                          setLines(next);
                        }}
                      />
                    </td>
                    <td className="num b">{money(line.quantity * line.rate)}</td>
                    <td>
                      <button
                        className="btn ghost icon sm"
                        aria-label={`Remove ${line.name}`}
                        onClick={() => setLines(lines.filter((_, i) => i !== index))}
                      >
                        &#10005;
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3}>Order value before GST</td>
                  <td className="num">{money(total)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        <p className="small muted">
          Quantities default to what is needed to reach par level and rates to the last
          moving-average cost. The order is created as a draft; someone other than its author has to
          approve it before goods can be received.
        </p>
      </div>
    </Modal>
  );
}

function SupplierModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const { fail, push } = useToast();
  const [form, setForm] = useState({
    name: '',
    gstin: '',
    phone: '',
    email: '',
    address: '',
    paymentTerms: 'NET_15',
    rating: 4,
    isActive: true,
  });

  const create = useMutation({
    mutationFn: () => api.post('/api/suppliers', form),
    onSuccess: () => {
      push('Supplier added', 'ok');
      onDone();
    },
    onError: fail,
  });

  return (
    <Modal
      title="New supplier"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn primary"
            disabled={!form.name.trim() || create.isPending}
            onClick={() => create.mutate()}
          >
            Add supplier
          </button>
        </>
      }
    >
      <div className="form-grid">
        <TextInput
          className="span2"
          label="Name *"
          autoFocus
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
        <TextInput
          label="GSTIN"
          value={form.gstin}
          onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })}
        />
        <TextInput
          label="Phone"
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
        />
        <TextInput
          label="Email"
          type="email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
        />
        <Select
          label="Payment terms"
          value={form.paymentTerms}
          onChange={(e) => setForm({ ...form, paymentTerms: e.target.value })}
        >
          <option value="ADVANCE">Advance</option>
          <option value="NET_7">Net 7</option>
          <option value="NET_15">Net 15</option>
          <option value="NET_30">Net 30</option>
        </Select>
        <TextInput
          className="span2"
          label="Address"
          value={form.address}
          onChange={(e) => setForm({ ...form, address: e.target.value })}
        />
      </div>
    </Modal>
  );
}
